/* eslint-disable @typescript-eslint/ban-ts-comment */
import { ToolId, TOOLS } from '../../products/registry';

export enum ProductName {
  Manager = 'Manager',
  ContactCenter = 'ContactCenter',
  Analytics = 'Analytics',
  Admin = 'Admin',
  ProStudio = 'ProStudio',
  Maestro = 'Maestro',
}

export type BaseURLsToTools = Record<ProductName, string>;

/**
 * Typed `Record`, so adding a `ProductName` without pointing it at a registry
 * entry fails the typecheck rather than silently producing a tool with no URL.
 */
const TOOL_BY_PRODUCT: Record<ProductName, ToolId> = {
  [ProductName.Manager]: 'manager',
  [ProductName.ContactCenter]: 'contactCenter',
  [ProductName.Analytics]: 'analytics',
  [ProductName.Admin]: 'admin',
  [ProductName.ProStudio]: 'proStudio',
  [ProductName.Maestro]: 'maestro',
};

enum Environment {
  Production = 'production',
  Candidate = 'candidate',
  QA1 = 'qa1',
  UAT = 'uat',
  EU = 'eu',
  Main = 'main',
}

type LowercaseEnum<T> = {
  // @ts-ignore
  [K in keyof T]: Lowercase<T[K]>;
};

export const createEnumObject = (
  enumObj: typeof ProductName
): LowercaseEnum<typeof ProductName> => {
  const result: Partial<LowercaseEnum<typeof ProductName>> = {};

  for (const [key, value] of Object.entries(enumObj)) {
    // @ts-ignore
    result[key as keyof typeof enumObj] = (
      value as string
    ).toLowerCase() as Lowercase<typeof value>;
  }

  return result as LowercaseEnum<typeof ProductName>;
};

const company = 'pypestream';
const topLevelDomains = {
  productionUS: 'com',
  productionEU: 'eu',
  development: 'dev',
};
const productionAUSuffix = `au.${company}.${topLevelDomains.productionUS}`;
const managerLocal = 'localhost:5173';
const proStudioLocal = 'localhost:5174';
const maestroLocal = 'localhost:5175';
const analyticsLocal = 'localhost.pypestream.dev:8000';

/**
 * A local dev server can be pointed at any environment's backend, which the
 * page's address can't tell. The app says which one by setting this global at
 * boot (Pro Studio does, from its GraphQL endpoint); a global rather than a
 * module variable because the smart components can load as separate bundles.
 */
export const LOCAL_BACKEND_ENV_KEY = '__PS_LOCAL_BACKEND_ENV__';

const localBackendEnvironment = ():
  | Environment.Candidate
  | Environment.UAT
  | Environment.Main
  | null => {
  const value = (globalThis as Record<string, unknown>)[LOCAL_BACKEND_ENV_KEY];
  return value === Environment.Candidate ||
    value === Environment.UAT ||
    value === Environment.Main
    ? value
    : null;
};

const PRODUCTS = Object.values(ProductName);

const hostTokenFor = (product: ProductName): string =>
  TOOLS[TOOL_BY_PRODUCT[product]].hostToken;

/**
 * Every environment is `https://<hostToken>.<suffix>` — `suffix` carries the
 * environment segment where there is one (`main.pypestream.dev`) and is bare in
 * production (`pypestream.com`). Deriving all of them from one table is what
 * makes registering a tool a single edit; each environment used to hand-list
 * every product, so a new tool meant seven edits and a miss went unnoticed.
 */
const urlsForSuffix = (suffix: string): BaseURLsToTools =>
  Object.fromEntries(
    PRODUCTS.map((product) => [
      product,
      `https://${hostTokenFor(product)}.${suffix}`,
    ])
  ) as BaseURLsToTools;

const developmentSuffix = (environment: string) =>
  `${environment}.${company}.${topLevelDomains.development}`;

const MAIN_URLS = urlsForSuffix(developmentSuffix(Environment.Main));

/**
 * Hosts the build was told about, for the tools that get a per-commit preview deployment.
 * Read statically so Vite substitutes them; this package compiles from source inside each app's
 * build, so these carry the consuming app's values.
 */
const injectedPreviewHosts = (): Partial<BaseURLsToTools> => ({
  [ProductName.Manager]: import.meta.env.FE_MANAGER_URL,
  [ProductName.ProStudio]: import.meta.env.FE_PRO_STUDIO_URL,
  [ProductName.Maestro]: import.meta.env.FE_MAESTRO_STUDIO_URL,
});

// Callers all build paths as `${baseUrl}/${id}`, so a trailing slash both yields `//` and makes
// an injected host compare unequal to the identical derived one. `.env.local` ships one today.
const withoutTrailingSlash = (url: string) => url.replace(/\/+$/, '');

/**
 * A preview host is only knowable from a build variable: `location` names the app you are in,
 * never which siblings were deployed at the same commit, so without one a sibling stays on main.
 */
const withInjectedPreviewHosts = (
  derived: BaseURLsToTools
): BaseURLsToTools => {
  const result = { ...derived };

  for (const [name, raw] of Object.entries(injectedPreviewHosts())) {
    const product = name as ProductName;
    const fallback = MAIN_URLS[product];
    const injected = raw && withoutTrailingSlash(raw);

    // Only a slot that fell back to main is open. Every non-preview deploy sets these to the
    // host derivation already produced, and Pro Studio's preview build injects the MAIN manager
    // host while derivation resolves the per-commit one -- letting the variable win there would
    // break the one cross-tool preview link that already works.
    if (injected && derived[product] === fallback && injected !== fallback) {
      result[product] = injected;
    }
  }

  return result;
};

const deriveBaseURLsFromLocation = (location: Location): BaseURLsToTools => {
  const { host } = location;

  const environments = {
    productionUS: urlsForSuffix(`${company}.${topLevelDomains.productionUS}`),
    productionEU: urlsForSuffix(`${company}.${topLevelDomains.productionEU}`),
    productionAU: urlsForSuffix(productionAUSuffix),
    candidate: urlsForSuffix(developmentSuffix(Environment.Candidate)),
    uat: urlsForSuffix(developmentSuffix(Environment.UAT)),
    main: MAIN_URLS,
    preview: (
      dynamicPart: string | null,
      appName: ProductName
    ): BaseURLsToTools => ({
      ...MAIN_URLS,
      [ProductName.Manager]:
        dynamicPart &&
        (appName === ProductName.Manager || appName === ProductName.ProStudio)
          ? `https://${dynamicPart}.${hostTokenFor(
              ProductName.Manager
            )}.${developmentSuffix(Environment.Main)}`
          : MAIN_URLS[ProductName.Manager],
      [ProductName.Analytics]:
        dynamicPart && appName === ProductName.Analytics
          ? `https://${hostTokenFor(
              ProductName.Analytics
            )}-pr-${dynamicPart}.preview.${company}.${
              topLevelDomains.development
            }`
          : MAIN_URLS[ProductName.Analytics],
      [ProductName.ProStudio]:
        dynamicPart && appName === ProductName.ProStudio
          ? `https://${dynamicPart}.${hostTokenFor(
              ProductName.ProStudio
            )}.${developmentSuffix(Environment.Main)}`
          : MAIN_URLS[ProductName.ProStudio],
      [ProductName.Maestro]:
        dynamicPart && appName === ProductName.Maestro
          ? `https://${dynamicPart}.${hostTokenFor(
              ProductName.Maestro
            )}.${developmentSuffix(Environment.Main)}`
          : MAIN_URLS[ProductName.Maestro],
    }),
    // A local app links to the other tools in the environment its backend is
    // on (see `LOCAL_BACKEND_ENV_KEY`), main when it hasn't said. Empty string
    // means "same origin" — the app being served locally links to itself with
    // relative URLs.
    local: (appName: ProductName): BaseURLsToTools => {
      const backend = localBackendEnvironment();
      const base = backend ? environments[backend] : MAIN_URLS;
      return {
        ...base,
        [ProductName.Manager]:
          appName === ProductName.Manager ? '' : base[ProductName.Manager],
        [ProductName.Analytics]:
          appName === ProductName.Analytics
            ? `https://${analyticsLocal}`
            : base[ProductName.Analytics],
        [ProductName.ProStudio]:
          appName === ProductName.ProStudio ? '' : base[ProductName.ProStudio],
        [ProductName.Maestro]:
          appName === ProductName.Maestro ? '' : base[ProductName.Maestro],
      };
    },
    qa1: urlsForSuffix(developmentSuffix(Environment.QA1)),
  };

  if (host.endsWith(`.${productionAUSuffix}`)) {
    return environments.productionAU;
  }
  if (host.endsWith(`.${company}.${topLevelDomains.productionUS}`)) {
    return environments.productionUS;
  }
  if (host.endsWith(`.${company}.${topLevelDomains.productionEU}`)) {
    return environments.productionEU;
  }

  // Preview hosts must be checked BEFORE the main-environment check, or
  // "cdc78c2452.prostudio.main.pypestream.dev" resolves as plain main.
  const commitHashManagerRegex = new RegExp(
    `^[a-z0-9]{10}\\.${hostTokenFor(ProductName.Manager)}\\.${company}\\.${
      topLevelDomains.development
    }$`
  );

  if (commitHashManagerRegex.test(host)) {
    const commitHash = host.split('.')[0];
    return environments.preview(commitHash, ProductName.Manager);
  }

  const analyticsPreviewRegex = new RegExp(
    `^${hostTokenFor(
      ProductName.Analytics
    )}-pr-(\\d+)\\.preview\\.${company}\\.${topLevelDomains.development}$`
  );

  if (analyticsPreviewRegex.test(host)) {
    const match = host.match(analyticsPreviewRegex);
    const prNumber = match ? match[1] : null;
    return environments.preview(prNumber, ProductName.Analytics);
  }

  const proStudioPreviewRegex = new RegExp(
    `^[a-z0-9]{10}\\.${hostTokenFor(
      ProductName.ProStudio
    )}\\.${developmentSuffix(Environment.Main)}$`
  );

  if (proStudioPreviewRegex.test(host)) {
    const commitHash = host.split('.')[0];
    return environments.preview(commitHash, ProductName.ProStudio);
  }

  if (
    host ===
      `${hostTokenFor(ProductName.Manager)}.${company}.${
        topLevelDomains.development
      }` ||
    host.endsWith(`.${developmentSuffix(Environment.Main)}`)
  ) {
    return environments.main;
  }
  if (host.endsWith(`.${developmentSuffix(Environment.UAT)}`)) {
    return environments.uat;
  }
  if (host.endsWith(`.${developmentSuffix(Environment.Candidate)}`)) {
    return environments.candidate;
  }
  if (host.endsWith(`.${developmentSuffix(Environment.QA1)}`)) {
    return environments.qa1;
  }

  if (host === managerLocal) {
    return environments.local(ProductName.Manager);
  }
  if (host === proStudioLocal) {
    return environments.local(ProductName.ProStudio);
  }
  if (host === maestroLocal) {
    return environments.local(ProductName.Maestro);
  }
  if (host === analyticsLocal) {
    return environments.local(ProductName.Analytics);
  }

  const productPattern = PRODUCTS.map(hostTokenFor).join('|');
  const newEnvironmentRegex = new RegExp(
    `^(${productPattern})\\.([a-z0-9-]+)\\.${company}\\.${topLevelDomains.development}$`
  );
  const newEnvMatch = host.match(newEnvironmentRegex);

  if (newEnvMatch) {
    const product = newEnvMatch[1];
    const environment = newEnvMatch[2];
    const rest = host.split(`${product}.${environment}.`)[1];

    return urlsForSuffix(`${environment}.${rest}`);
  }

  return environments.main;
};

export const generateBaseURLsToTools = (location: Location): BaseURLsToTools =>
  withInjectedPreviewHosts(deriveBaseURLsFromLocation(location));
